import React from 'react'
import cx from 'classnames'
import { l } from '@manychat/manyui'

import { AddonPromotionStep } from 'apps/aiTextImprover/components/Steps/AddonPromotionStep/AddonPromotionStep'
import { ChooseGoalStep } from 'apps/aiTextImprover/components/Steps/ChooseGoalStep/ChooseGoalStep'
import { ChooseImprovementStep } from 'apps/aiTextImprover/components/Steps/ChooseImprovementStep/ChooseImprovementStep'
import { RateResultStep } from 'apps/aiTextImprover/components/Steps/RateResultStep/RateResultStep'
import { WriteTextStep } from 'apps/aiTextImprover/components/Steps/WriteTextStep/WriteTextStep'
import { useAiTextImprover } from 'apps/aiTextImprover/hooks/useAiTextImprover'
import { AiTextImproverAnalytics } from 'apps/aiTextImprover/lib/analytics'
import { Goals } from 'apps/aiTextImprover/lib/config'
import { OnImprovementSubmit } from 'apps/aiTextImprover/lib/signatures'
import { Step } from 'apps/aiTextImprover/lib/types'
import { TextImproverValidationRules } from 'apps/aiTextImprover/lib/validation'
import { withErrorBoundary } from 'common/core/components/ErrorBoundary'
import { useDidMountEffect } from 'utils/commonHooks'

import cm from './AiTextImprover.module.css'

export interface AiTextImproverProps {
  initialText: string
  onSubmit: OnImprovementSubmit
  newTextMaxLength?: number
  flowNs?: string
  className?: string
}

const Component = React.memo(function AiTextImprover({
  initialText,
  onSubmit,
  newTextMaxLength = TextImproverValidationRules.textToImprove.max,
  flowNs,
  className,
}: AiTextImproverProps) {
  useDidMountEffect(() => {
    AiTextImproverAnalytics.logOpen({ hasText: initialText.length > 0 })

    return () => {
      AiTextImproverAnalytics.logClose()
    }
  })

  const {
    activeStep,
    text,
    submitNewText,
    goal,
    subGoal,
    selectGoal,
    openPreviousStep,
    submitImprovedText,
    generationData,
    submitRating,
    submitUpgrade,
  } = useAiTextImprover({
    initialText,
    onSubmit,
    flowNs,
  })

  const title =
    subGoal ??
    l.getString(Goals.find((goalOption) => goalOption.value === goal)?.config.label) ??
    ''

  return (
    <div className={cx(cm.wrapper, className)}>
      {activeStep === Step.WRITE_TEXT && (
        <WriteTextStep text={text} onSubmit={submitNewText} maxLength={newTextMaxLength} />
      )}

      {activeStep === Step.CHOOSE_GOAL && <ChooseGoalStep options={Goals} onSelect={selectGoal} />}

      {activeStep === Step.CHOOSE_IMPROVEMENT && generationData && (
        <ChooseImprovementStep
          title={title}
          onSelect={submitImprovedText}
          generationData={generationData}
          handleBack={openPreviousStep}
        />
      )}

      {activeStep === Step.RATE_RESULT && <RateResultStep onRatingSubmit={submitRating} />}

      {activeStep === Step.PROMOTE_ADDON && <AddonPromotionStep onUpgradeSubmit={submitUpgrade} />}
    </div>
  )
})

Component.displayName = 'AiTextImprover'

export const AiTextImprover = withErrorBoundary(Component, {
  scope: 'ai',
  section: 'ai_text_improver',
})
